import { postTask } from "../worker/DataWorkerManager.ts";

type SubscriptionBatch = {
    key: string;
    subscriptions: Array<{
        callback: (result: SubscriptionBatchResult) => void;
    }>;
    payload: any;
    requestTime: number;
    contextId?: string;
    sessionId: number;
};

export type SubscriptionBatchResult =
    | { ok: true; data: any; row: number; channel: number; found: boolean; version: number }
    | { ok: false; error: unknown };

export class SubscriptionBatchCoordinator {
    private pendingBatches = new Map<string, SubscriptionBatch>();
    private inflightRequests = new Set<string>();
    private flushScheduled = false;
    private _sessionId = 0;

    /* see docs/EXTRACTED_COMMENTS.md#c216 */
    public setSessionId(id: number): void {
        this._sessionId = id;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c217 */
    public bumpSession(): number {
        this._sessionId += 1;
        return this._sessionId;
    }

    public async requestSubscriptionUpdate(
        payload: any,
        callback: (result: SubscriptionBatchResult) => void,
        contextId?: string
    ): Promise<void> {
        // see docs/EXTRACTED_COMMENTS.md#c218
        // Empty adopt maps serialize as "{}" — no key concat in the common case.
        const itemText: string = payload?.itemText ?? "";
        const adoptText: string = payload?.adoptText ?? "{}";
        const batchKey = adoptText === "{}" ? itemText : `${itemText}\0${adoptText}`;

        let batch = this.pendingBatches.get(batchKey);
        if (!batch) {
            const wireId = this.generateRandomKey();
            batch = {
                key: wireId,
                subscriptions: [],
                payload: { ...(payload || {}), itemID: wireId },
                requestTime: Date.now(),
                contextId,
                sessionId: this._sessionId,
            };
            this.pendingBatches.set(batchKey, batch);
        }
        batch.subscriptions.push({ callback });

        this.scheduleFlush();
    }

    private scheduleFlush(): void {
        if (this.flushScheduled) return;
        if (this.pendingBatches.size === 0) return;
        this.flushScheduled = true;
        queueMicrotask(() => this.flushBatches());
    }

    private flushBatches(): void {
        // see docs/EXTRACTED_COMMENTS.md#c603
        this.flushScheduled = false;

        const batches = Array.from(this.pendingBatches.values());
        if (batches.length === 0) return;

        this.pendingBatches.clear();

        for (const b of batches) {
            if (b.sessionId !== this._sessionId) continue;
            try {
                this.processBatchDetached(b);
            } catch (error) {
                console.error('[SubscriptionBatchCoordinator] Error during flush:', error);
            }
        }
    }

    private processBatchDetached(batch: SubscriptionBatch): void {
        const { key, subscriptions, payload, sessionId } = batch;
        if (subscriptions.length === 0) return;

        this.inflightRequests.add(key);

        const resultPromise = postTask(key, payload, 'batch-coordinator');
        resultPromise.then((result) => {
            if (sessionId !== this._sessionId) return;
            const {data, row, channel, found, version} = result || {};
            for (const { callback } of subscriptions) {
                try {
                    callback({ ok: true, data, row, channel, found, version });
                } catch (error) {
                    console.error(`[SubscriptionBatchCoordinator] Callback error:`, error);
                }
            }
        }).catch((error) => {
            if (sessionId !== this._sessionId) return;
            console.error(`[SubscriptionBatchCoordinator] Batch ${key} failed:`, error);
            for (const { callback } of subscriptions) {
                try {
                    callback({ ok: false, error });
                } catch (callbackError) {
                    console.error(`[SubscriptionBatchCoordinator] Callback error during failure settlement:`, callbackError);
                }
            }
        }).finally(() => {
            this.inflightRequests.delete(key);
        });
    }

    private generateRandomKey(): string {
        return `${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    }

    public getStats() {
        const now = Date.now();
        let totalQueuedSubscriptions = 0;
        let oldestRequestAt = Number.POSITIVE_INFINITY;
        let newestRequestAt = 0;
        const bulkGroups = new Set<string>();
        const sizes: number[] = [];

        for (const b of this.pendingBatches.values()) {
            totalQueuedSubscriptions += b.subscriptions.length;
            sizes.push(b.subscriptions.length);
            oldestRequestAt = Math.min(oldestRequestAt, b.requestTime);
            newestRequestAt = Math.max(newestRequestAt, b.requestTime);
            if (b.contextId) bulkGroups.add(b.contextId);
        }

        const queuedAgeMs = this.pendingBatches.size > 0 ? Math.max(0, now - oldestRequestAt) : 0;
        const newestAgeMs = this.pendingBatches.size > 0 ? Math.max(0, now - newestRequestAt) : 0;
        const sizeSummary = (() => {
            if (sizes.length === 0) return { mean: 0, min: 0, max: 0, count: 0 };
            const sum = sizes.reduce((a, b) => a + b, 0);
            return { mean: sum / sizes.length, min: Math.min(...sizes), max: Math.max(...sizes), count: sizes.length };
        })();

        return {
            pendingBatches: this.pendingBatches.size,
            inflightRequests: this.inflightRequests.size,
            totalQueuedSubscriptions,
            bulkGroups: bulkGroups.size,
            queuedAgeMs,
            newestAgeMs,
            sizeSummary,
            flushScheduled: this.flushScheduled,
        };
    }

    public clear(): void {
        // see docs/EXTRACTED_COMMENTS.md#c604
        this.flushScheduled = false;
        this.pendingBatches.clear();
        this.inflightRequests.clear();
    }
}

let coordinatorInstance: SubscriptionBatchCoordinator | null = null;

export function getBatchCoordinator(): SubscriptionBatchCoordinator {
    if (!coordinatorInstance) {
        coordinatorInstance = new SubscriptionBatchCoordinator();
    }
    return coordinatorInstance;
}

export function resetBatchCoordinator(): void {
    if (coordinatorInstance) {
        // see docs/EXTRACTED_COMMENTS.md#c605
        const next = coordinatorInstance.bumpSession();
        coordinatorInstance.clear();
        // see docs/EXTRACTED_COMMENTS.md#c606
        void next;
    }
}

